import { Form, Input, notification, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AuthLayout from 'components/layout/auth-layout/auth-layout.component';
import { useQuery } from 'hooks/useQuery';
import { useResetPasswordMutation, useVerifyResetPasswordTokenMutation } from '../services/authApi';
import lockSvg from 'assets/images/auth/lock.svg';
import expiredSvg from 'assets/images/auth/expired.svg';
import { SigninFormItem } from '../components/signin-form-item/signin-form-item';
import { AuthButtonComponent } from '../components/auth-button/auth-button.component';
import { useForm } from 'antd/lib/form/Form';
import { notificationDefaultError } from 'utils/ui';
import { PasswordField } from '../components/password-field/password-field';

const ResetPassword = () => {
  const query = useQuery();
  const idToken = query.get('id');
  const history = useHistory();
  const [form] = useForm();

  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [verifyResetPasswordToken, verifyStatus] = useVerifyResetPasswordTokenMutation();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  useEffect(() => {
    if (idToken) {
      localStorage.setItem('resetPasswordToken', JSON.stringify(idToken));
    }

    const verifyToken = async () => {
      const resetPasswordToken = JSON.parse(localStorage.getItem('resetPasswordToken') ?? '');
      await verifyResetPasswordToken({ resetPasswordToken }).unwrap();
    };

    verifyToken();
  }, []);

  useEffect(() => {
    if (!verifyStatus.isUninitialized && !verifyStatus.isLoading) {
      if (verifyStatus.isSuccess) {
        setIsVerified(true);
      } else {
        setIsVerified(false);
      }
    }
  }, [verifyStatus]);

  const onSubmit = async (formState: any) => {
    try {
      const resetPasswordToken = JSON.parse(localStorage.getItem('resetPasswordToken') ?? '');
      const { confirmPassword, ...rest } = formState;
      const userResponse = await resetPassword({ resetPasswordToken, ...rest }).unwrap();
      if (userResponse) {
        history.replace('/signin');
        localStorage.clear();
        notification.success({
          message: 'Password reset',
          description: 'Password updated successfully!, Please login with new password',
          placement: 'topRight',
        });
      }
    } catch (_err) {
      notificationDefaultError();
    }
  };

  const onFinishFailed = () => {
    // console.log('Failed:', errorInfo);
  };

  return (
    <AuthLayout showGotoSignin>
      {isVerified === null && (
        <div className="flex justify-content-center align-items-center" style={{ height: '350px' }}>
          <Spin />
        </div>
      )}

      {isVerified === true && (
        <div className="flex align-items-start mt-8" style={{ letterSpacing: '0.5px' }}>
          <div className="mr-4">
            <img src={lockSvg} alt="key" />
          </div>

          <div className="flex-1">
            <p className="text-base font-semibold mb-2" style={{ color: '#050D43' }}>
              Create new password
            </p>
            <p style={{ color: '#6B7D8B' }}>Your password must be different from the previous used password.</p>

            <Form
              form={form}
              name="basic"
              onFinish={onSubmit}
              onFinishFailed={onFinishFailed}
              size="large"
              layout="vertical"
              autoComplete="off"
              className="fadein animation-duration-500 w-full mt-6"
            >
              {!idToken && (
                <SigninFormItem
                  form={form}
                  formName="oldPassword"
                  label="Old Password"
                  className="mb-5"
                  formRules={[
                    {
                      required: true,
                      message: 'Please enter your old password!',
                    },
                  ]}
                >
                  <Input.Password />
                </SigninFormItem>
              )}

              <PasswordField form={form} isSigninField className="mb-5" name="newPassword" />

              <SigninFormItem
                form={form}
                formName="confirmPassword"
                className="mb-5"
                label="Confirm Password"
                formDependencies={['newPassword']}
                formRules={[
                  {
                    required: true,
                    message: 'Please input confirm password',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords that you entered do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </SigninFormItem>

              <Form.Item className="mt-7 text-right">
                <AuthButtonComponent isLoading={isLoading}>Reset password</AuthButtonComponent>
              </Form.Item>
            </Form>
          </div>
        </div>
      )}

      {isVerified === false && (
        <div
          className="flex align-items-start mt-8"
          style={{ letterSpacing: '0.5px', maxWidth: '400px', margin: 'auto' }}
        >
          <div className="mr-4">
            <img src={expiredSvg} alt="key" />
          </div>

          <div className="flex-1">
            <p className="text-base font-semibold mb-2" style={{ color: '#050D43' }}>
              Reset password link expired
            </p>
            <p style={{ color: '#6B7D8B' }}>To generate new link please click below.</p>

            <AuthButtonComponent className="mt-7" onClick={() => history.push('/forgot-password')}>
              Go to forgot password
            </AuthButtonComponent>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
